import React, { useState, useEffect } from 'react';
import { adminApiClient, User } from '../lib/api';
import {
  Search,
  Edit2,
  Trash2,
  CheckCircle,
  XCircle,
  RefreshCw,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { showDeleteConfirmation } from '../lib/sweetAlert';

interface UserFormData {
  name: string;
  email: string;
  phone: string;
  role: 'super_admin' | 'temple_admin' | 'priest' | 'devotee';
  status: 'active' | 'inactive' | 'suspended';
  templeId?: string;
}

// Compact Switch Component
const Switch = ({ checked, onChange, disabled = false }: { checked: boolean; onChange: () => void; disabled?: boolean }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={onChange}
    disabled={disabled}
    className={`
      relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
      transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:ring-offset-2
      ${checked ? 'bg-green-500' : 'bg-gray-200'}
      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
    `}
  >
    <span
      className={`
        pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 
        transition duration-200 ease-in-out
        ${checked ? 'translate-x-4' : 'translate-x-0'}
      `}
    />
  </button>
);

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [temples, setTemples] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  const [formData, setFormData] = useState<UserFormData>({
    name: '',
    email: '',
    phone: '',
    role: 'devotee',
    status: 'active',
    templeId: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersRes, templesRes] = await Promise.all([
        adminApiClient.getUsers(),
        adminApiClient.getTemples()
      ]);

      if (usersRes.success && usersRes.data) {
        const mappedUsers = usersRes.data.users
          .map((user: any) => ({
            ...user,
            id: user._id || user.id,
            status: user.isActive ? 'active' : 'inactive' // Map backend boolean to frontend status
          }))
          .filter((user: any) => user.role !== 'admin' && user.role !== 'super_admin'); // Exclude system admins
        setUsers(mappedUsers);
      } else {
        toast.error(usersRes.message || 'Failed to fetch users');
      }

      if (templesRes.success && templesRes.data) {
        const mappedTemples = templesRes.data.temples.map((temple: any) => ({
          ...temple,
          id: temple._id || temple.id
        }));
        setTemples(mappedTemples);
      }
    } catch (err) {
      toast.error('Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const loadingToast = toast.loading(editingUser ? 'Updating user...' : 'Creating user...');

    try {
      if (editingUser) {
        const updateData = { ...formData };
        if (updateData.role === 'super_admin' || updateData.role === 'devotee') {
          delete updateData.templeId;
        } else if (!updateData.templeId) {
          updateData.templeId = '';
        }

        const response = await adminApiClient.updateUser(editingUser.id, updateData);
        if (response.success) {
          await fetchData();
          setShowForm(false);
          setEditingUser(null);
          resetForm();
          toast.success('User updated successfully', { id: loadingToast });
        } else {
          const msg = response.errors ? response.errors.map((e: any) => e.msg).join(', ') : (response.message || 'Failed to update');
          toast.error(msg, { id: loadingToast });
        }
      } else {
        toast.error('User creation should be done physically via registration', { id: loadingToast });
      }
    } catch (err) {
      toast.error('Error saving user', { id: loadingToast });
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      phone: user.phone || '',
      role: user.role as any,
      status: (user.status || 'active') as any,
      templeId: user.templeId || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (userId: string) => {
    const isConfirmed = await showDeleteConfirmation(
      'Delete User?',
      'This action cannot be undone. The user will be permanently removed.'
    );

    if (!isConfirmed) return;

    const loadingToast = toast.loading('Deleting user...');
    try {
      const response = await adminApiClient.deleteUser(userId);
      if (response.success) {
        setUsers(users.filter(u => u.id !== userId));
        toast.success('User deleted', { id: loadingToast });
      } else {
        toast.error(response.message || 'Failed to delete', { id: loadingToast });
      }
    } catch (err) {
      toast.error('Error deleting user', { id: loadingToast });
    }
  };

  const handleStatusToggle = async (user: User) => {
    // Determine current and new status
    const currentStatus = user.status || (user.isActive ? 'active' : 'inactive');
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    const newIsActive = newStatus === 'active';

    // Optimistic Update
    const originalUsers = [...users];
    setUsers(users.map(u => u.id === user.id ? { ...u, status: newStatus, isActive: newIsActive } : u));

    try {
      const response = await adminApiClient.updateUser(user.id, { status: newStatus });
      if (response.success && response.data && response.data.user) {
        // Confirm update with real data from server
        const updatedUser = response.data.user;
        setUsers(current => current.map(u => u.id === user.id ? {
          ...u,
          ...updatedUser,
          id: updatedUser._id || updatedUser.id || u.id,
          status: updatedUser.isActive ? 'active' : 'inactive',
          isActive: updatedUser.isActive
        } : u));

        toast.success(`User ${newStatus === 'active' ? 'activated' : 'deactivated'}`, {
          icon: newStatus === 'active' ? '✅' : '⏸️',
          duration: 2000
        });
      } else {
        // Revert on failure
        setUsers(originalUsers);
        toast.error('Failed to update status');
      }
    } catch (err) {
      setUsers(originalUsers);
      toast.error('Error updating status');
    }
  };

  const handleBulkAction = async (action: string) => {
    if (selectedUsers.length === 0) return;

    // Optimistic Update for Bulk
    const originalUsers = [...users];
    const newStatus = action === 'activate' ? 'active' : 'inactive';

    setUsers(users.map(u => selectedUsers.includes(u.id) ? { ...u, status: newStatus } : u));

    const loadingToast = toast.loading(`Processing ${selectedUsers.length} users...`);

    try {
      const promises = selectedUsers.map(userId =>
        adminApiClient.updateUser(userId, { status: newStatus })
      );

      await Promise.all(promises); // Note: Ideally backend should have a bulk endpoint

      toast.success('Bulk action completed', { id: loadingToast });
      setSelectedUsers([]);
      // No need to fetch if optimistic update was correct, but safer to refetch in bulk
      fetchData();
    } catch (err) {
      setUsers(originalUsers);
      toast.error('Error in bulk action', { id: loadingToast });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      role: 'devotee',
      status: 'active',
      templeId: ''
    });
    setEditingUser(null);
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    const matchesStatus = filterStatus === 'all' || (user.status || 'active') === filterStatus;
    return matchesSearch && matchesRole && matchesStatus;
  });

  const getRoleBadge = (role: string) => {
    const styles = {
      super_admin: 'bg-red-50 text-red-700 border-red-100',
      temple_admin: 'bg-purple-50 text-purple-700 border-purple-100',
      priest: 'bg-[color-mix(in_srgb,var(--color-secondary)_10%,white)] text-[var(--color-secondary)] border-[color-mix(in_srgb,var(--color-secondary)_20%,white)]',
      devotee: 'bg-green-50 text-green-700 border-green-100',
    } as const;

    const label = role.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    const style = styles[role as keyof typeof styles] || 'bg-gray-50 text-gray-700 border-gray-100';

    return (
      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${style} whitespace-nowrap`}>
        {label}
      </span>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            Users
            <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-sm font-medium">
              {users.length}
            </span>
          </h1>
          <p className="text-gray-500 text-sm mt-1">Manage user access and permissions</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchData}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
          </button>

          {selectedUsers.length > 0 && (
            <div className="flex bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
              <button
                onClick={() => handleBulkAction('activate')}
                className="px-3 py-2 text-xs font-medium text-green-700 hover:bg-green-50 border-r border-gray-200 flex items-center gap-1"
              >
                <CheckCircle size={14} /> Activate ({selectedUsers.length})
              </button>
              <button
                onClick={() => handleBulkAction('deactivate')}
                className="px-3 py-2 text-xs font-medium text-amber-700 hover:bg-amber-50 flex items-center gap-1"
              >
                <XCircle size={14} /> Deactivate ({selectedUsers.length})
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 mb-6 space-y-4 md:space-y-0 md:flex md:items-center md:gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search users by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20 focus:border-[var(--color-primary)] transition-all"
          />
        </div>
        <div className="flex items-center gap-3">
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[var(--color-primary)] bg-white"
          >
            <option value="all">All Roles</option>
            {/* <option value="super_admin">Super Admin</option> Hidden for users page */}
            <option value="temple_admin">Temple Admin</option>
            <option value="priest">Priest</option>
            <option value="devotee">Devotee</option>
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[var(--color-primary)] bg-white"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
      </div>

      {/* Table Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50 text-gray-500 font-medium border-b border-gray-200">
              <tr>
                <th className="py-3 px-4 w-10">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-[var(--color-primary)] focus:ring-[var(--color-primary)]"
                    checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                    onChange={(e) => {
                      setSelectedUsers(e.target.checked ? filteredUsers.map(u => u.id) : []);
                    }}
                  />
                </th>
                <th className="py-3 px-4">User Details</th>
                <th className="py-3 px-4">Role</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50/50 transition-colors group">
                    <td className="py-3 px-4">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-[var(--color-primary)] focus:ring-[var(--color-primary)]"
                        checked={selectedUsers.includes(user.id)}
                        onChange={(e) => {
                          setSelectedUsers(e.target.checked
                            ? [...selectedUsers, user.id]
                            : selectedUsers.filter(id => id !== user.id)
                          );
                        }}
                      />
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[color-mix(in_srgb,var(--color-primary)_12%,white)] to-[color-mix(in_srgb,var(--color-primary)_22%,white)] text-[color-mix(in_srgb,var(--color-primary)_80%,black)] flex items-center justify-center font-semibold text-sm">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{user.name}</div>
                          <div className="text-xs text-gray-500">{user.email}</div>
                          {user.phone && <div className="text-xs text-gray-400 mt-0.5">{user.phone}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {getRoleBadge(user.role)}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={(user.status || 'active') === 'active'}
                          onChange={() => handleStatusToggle(user)}
                        />
                        <span className={`text-xs font-medium ${(user.status || 'active') === 'active' ? 'text-green-600' : 'text-gray-400'}`}>
                          {(user.status || 'active') === 'active' ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(user)}
                          className="p-1.5 text-gray-500 hover:text-[var(--color-secondary)] hover:bg-[color-mix(in_srgb,var(--color-secondary)_10%,white)] rounded-lg transition-colors"
                          title="Edit User"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(user.id)}
                          className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete User"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-gray-500">
                    <div className="flex flex-col items-center justify-center">
                      <Search size={32} className="text-gray-300 mb-3" />
                      <p>No users found matching your criteria.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer/Pagination Placeholder */}
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 text-xs text-gray-500 flex justify-between items-center">
          <div>Showing {filteredUsers.length} of {users.length} users</div>
          {/* Add pagination here if needed */}
        </div>
      </div>

      {/* Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <h3 className="font-semibold text-gray-900">{editingUser ? 'Edit User' : 'Add New User'}</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                <XCircle size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Full Name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[var(--color-primary)]/20 focus:border-[var(--color-primary)]"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Email Address</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[var(--color-primary)]/20 focus:border-[var(--color-primary)]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={e => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[var(--color-primary)]/20 focus:border-[var(--color-primary)]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Role</label>
                  <select
                    value={formData.role}
                    onChange={e => setFormData({ ...formData, role: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[var(--color-primary)]/20 focus:border-[var(--color-primary)] bg-white"
                  >
                    <option value="devotee">Devotee</option>
                    <option value="priest">Priest</option>
                    <option value="temple_admin">Temple Admin</option>
                    {/* <option value="super_admin">Super Admin</option> Hidden for users page */}
                  </select>
                </div>

                {(formData.role === 'temple_admin' || formData.role === 'priest') && (
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Assigned Temple</label>
                    <select
                      required
                      value={formData.templeId}
                      onChange={e => setFormData({ ...formData, templeId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[var(--color-primary)]/20 focus:border-[var(--color-primary)] bg-white"
                    >
                      <option value="">Select a temple...</option>
                      {temples.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-100 mt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-[var(--color-primary)] rounded-lg hover:opacity-90 transition-colors shadow-sm"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
